import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { authApi, onboardingApi } from '../services/api';
import { useApp } from '../AppContext';
import { AuthNavbar, WizardSteps } from './Auth';
import cFirstLogo from '../images/SwiftCheqLogo.png';

const REG_EMPLOYEE_SIZES = ['', '0-100', '201-500', '501-1,000', '1,001-5,000', '5,001-10,000', '10,000+'];
const REG_CONTACT_TYPES = ['Contact Person', 'Business Person', 'Cfirst Signing Authority', 'Client Signing Authority', 'Client POC', 'Client Finance POC', 'Client Invoicing Email'];
const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/;
const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]{1}$/;
const TAN_RE = /^[A-Z\d]{10}$/;
const NOT_APPLICABLE_VALUE = 'UNREGISTERED';
// Matches OnBoardingRepository.SignPreviewMarker (backend) — the placeholder the unsigned
// agreement preview HTML carries at the ##SIGN## tag's position, in place of an empty string.
const SIGN_PREVIEW_MARKER = '§§CF_SIGN_PREVIEW§§';
// Matches OnBoardingRepository.SignNamePreviewMarker (backend) — same idea, for the
// ##SIGNNAME## tag's position.
const SIGNNAME_PREVIEW_MARKER = '§§CF_SIGNNAME_PREVIEW§§';
function emptyRegContact() { return { contactType: '', contactName: '', designation: '', email: '', phone: '', mobile: '' }; }

function BillingField({ label, req, value, onChange, error, maxLength }) {
  return (
    <div className="fg">
      <label className="flbl">{label} {req && <span className="req">*</span>}</label>
      <input className="inp" value={value} maxLength={maxLength} onChange={e => onChange(e.target.value)}
        style={error ? { borderColor: 'var(--rd)' } : {}} />
      {error && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{error}</span>}
    </div>
  );
}

function FileField({ label, req, file, onChange, error, disabled, existing, changing, onToggleChange, onDownload }) {
  const showExisting = !disabled && existing && !changing && !file;
  return (
    <div className="fg">
      <label className="flbl">{label} {req && <span className="req">*</span>}</label>
      {showExisting ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button type="button" className="btn bout bsm" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={onDownload}>
            ⬇ {existing.name}
          </button>
          <button type="button" className="btn bghost bsm" onClick={() => onToggleChange(true)}>Change</button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label className="btn bout bsm" style={{
            display: 'block', flex: 1, cursor: disabled ? 'not-allowed' : 'pointer', margin: 0, textAlign: 'left',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1,
            ...(error ? { borderColor: 'var(--rd)' } : {}),
          }}>
            {file ? file.name : disabled ? 'Not required' : 'Select File'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={disabled} style={{ display: 'none' }}
              onChange={e => { onChange(e.target.files[0] || null); e.target.value = ''; }} />
          </label>
          {!disabled && existing && changing && (
            <button type="button" className="btn bghost bsm" onClick={() => { onToggleChange(false); onChange(null); }}>Cancel</button>
          )}
        </div>
      )}
      {error && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{error}</span>}
    </div>
  );
}

function NAField({ label, req, value, naChecked, naLabel, onChange, onNA, error }) {
  return (
    <div className="fg">
      <label className="flbl">{label} {req && <span className="req">*</span>}</label>
      <input className="inp" value={value} onChange={e => onChange(e.target.value)} disabled={naChecked}
        style={{ ...(naChecked ? { background: 'var(--g100)', color: 'var(--g400)' } : {}), ...(error && !naChecked ? { borderColor: 'var(--rd)' } : {}) }} />
      {error && !naChecked && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{error}</span>}
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--g600)', cursor: 'pointer', marginTop: '4px' }}>
        <input type="checkbox" checked={naChecked} onChange={e => onNA(e.target.checked)} />
        {naLabel}
      </label>
    </div>
  );
}

// ── Register Page (Company Info — step 1 of the post-login wizard) ──────
export function Register({ onNavigate, prefillCompanyName = '', prefillContactName = '', prefillEmail = '' }) {
  const { clientId, user, addToast } = useApp();
  const registeredContactName = prefillContactName || user?.UserDescription || '';
  const registeredEmail       = prefillEmail       || user?.EmailId         || '';
  const registeredPhone       = user?.Phone || user?.PhoneNumber || user?.Mobile || '';
  const [form, setForm] = useState({ clientName: prefillCompanyName, cinNumber: '', country: '', state: '', city: '', zipCode: '', industryType: '', employeeSize: '' });
  // First-time signup: the client has no saved Contact Persons yet, so this initial
  // row always defaults to "Client Invoicing Email" (the account's own email/phone
  // being known doesn't count as having contact info on file).
  const [contacts, setContacts] = useState([{
    ...emptyRegContact(),
    contactType: 'Client POC',
    contactName: registeredContactName,
    email:       registeredEmail,
    phone:       registeredPhone,
    mobile:      registeredPhone,
  }]);
  const [formErrors, setFormErrors] = useState({});
  const [billTo, setBillTo] = useState({ addr1: '', addr2: '', gstin: '', pan: '', tan: '' });
  const [billNA, setBillNA] = useState({ gstin: false, pan: false, tan: false });
  const [billErrors, setBillErrors] = useState({});
  const [panLbl, setPanLbl] = useState('Not Applicable');
  const [docFiles, setDocFiles] = useState({ gstin: null, pan: null, tan: null });
  const [docErrors, setDocErrors] = useState({});
  // Documents already uploaded in a previous visit to this page (e.g. navigating to
  // DigiSign and clicking "Back") — restored from getBasicInfo. { name, id } or null.
  const [existingDocs, setExistingDocs] = useState({ gstin: null, pan: null, tan: null });
  const [docChanging, setDocChanging] = useState({ gstin: false, pan: false, tan: false });
  const [tdsApplicable, setTdsApplicable] = useState(false);
  const gstValid = !billNA.gstin && GSTIN_RE.test(String(billTo.gstin || '').trim());
  useEffect(() => { if (!gstValid) setTdsApplicable(false); }, [gstValid]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [gstin, setGstin] = useState('');
  const [gstFetching, setGstFetching] = useState(false);
  const [gstResult, setGstResult] = useState(null);
  // Country/State are dropdowns keyed by lookup id, but the GST lookup only returns free-text
  // names — this holds those names until the matching dropdown option loads, so they can be
  // resolved to ids instead of being written into the <select> as raw text.
  const [pendingLocation, setPendingLocation] = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setContact(i, k, v) { setContacts(cs => cs.map((c, j) => j === i ? { ...c, [k]: v } : c)); }
  function addContact() { setContacts(cs => [...cs, emptyRegContact()]); }
  function removeContact(i) { setContacts(cs => cs.filter((_, j) => j !== i)); }
  function setBillToF(k, v) { setBillTo(b => ({ ...b, [k]: v })); }
  function handleBillNA(field, checked) {
    setBillNA(p => ({ ...p, [field]: checked }));
    setBillToF(field, checked ? NOT_APPLICABLE_VALUE : '');
    // GST marked "not applicable" → the GST document is no longer mandatory; drop
    // whatever was selected/errored so it doesn't get uploaded or block submission.
    if (field === 'gstin' && checked) {
      setDocFiles(d => ({ ...d, gstin: null }));
      setDocErrors(e => ({ ...e, gstin: '' }));
    }
  }
  function setDocFile(field, file) { setDocFiles(d => ({ ...d, [field]: file })); if (file) setDocErrors(e => ({ ...e, [field]: '' })); }
  function setDocChangingF(field, changing) { setDocChanging(d => ({ ...d, [field]: changing })); }
  async function downloadExistingDoc(fileName) {
    try {
      const { blob, filename } = await onboardingApi.downloadAttachment(fileName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { addToast('Download failed: ' + e.message, 'error'); }
  }

  // Note: this endpoint's JSON keys are all lowercase too (LowercaseNamingStrategy on the
  // backend) — e.g. `res.legalname`/`res.pannumber`, not `res.legalName`/`res.panNumber`.
  async function handleFetchGstin() {
    const g = gstin.trim().toUpperCase();
    if (!GSTIN_RE.test(g)) { addToast('Please enter a valid 15-digit GSTIN', 'error'); return; }
    // Carry the entered GSTIN into Billing info regardless of whether the lookup succeeds.
    setBillNA(p => ({ ...p, gstin: false }));
    setBillToF('gstin', g);
    setGstFetching(true);
    setGstResult(null);
    try {
      const res = await onboardingApi.verifyGstin(g);
      setForm(f => ({
        ...f,
        clientName: res.legalname || f.clientName,
        city:       res.city      || f.city,
        zipCode:    res.zip       || f.zipCode,
      }));
      if (res.address1) setBillToF('addr1', res.address1);
      if (res.address2) setBillToF('addr2', res.address2);
      if (res.pannumber) {
        setBillNA(p => ({ ...p, pan: false }));
        setBillToF('pan', res.pannumber);
      }
      if (res.country || res.state) setPendingLocation({ country: res.country || '', state: res.state || '' });
      setGstResult({ legalName: res.legalname || '', city: res.city || '', state: res.state || '', status: res.status || 'Active' });
      addToast('GSTIN verified — details auto-filled below', 'success');
    } catch (e) {
      addToast(e.message || 'GSTIN lookup failed — please fill in the details below manually.', 'error');
    } finally { setGstFetching(false); }
  }

  // Resolves the GST lookup's free-text country/state names to the ids the <select> dropdowns
  // use. Fetches the matched country's state list itself (rather than waiting on the
  // [form.country] effect below) so it always matches against the RIGHT country's list — that
  // effect's fetch is async, so watching its `states` result via a second effect risked matching
  // against whatever stale list happened to be there first and giving up before the real one arrived.
  useEffect(() => {
    if (!pendingLocation || countries.length === 0) return;
    let cancelled = false;

    (async () => {
      const countryMatch = countries.find(c =>
        (c.text ?? c.Text ?? '').trim().toLowerCase() === (pendingLocation.country || '').trim().toLowerCase());
      if (!countryMatch) { if (!cancelled) setPendingLocation(null); return; }

      const countryId = countryMatch.value ?? countryMatch.Value;
      set('country', countryId);

      if (!pendingLocation.state) { if (!cancelled) setPendingLocation(null); return; }

      try {
        const stateList = await onboardingApi.getStateList(countryId);
        if (cancelled) return;
        if (Array.isArray(stateList)) {
          setStates(stateList);
          const stateMatch = stateList.find(s =>
            (s.text ?? s.Text ?? '').trim().toLowerCase() === pendingLocation.state.trim().toLowerCase());
          if (stateMatch) set('state', stateMatch.value ?? stateMatch.Value);
        }
      } catch {
        // Lookup failed — leave State for the user to pick manually.
      } finally {
        if (!cancelled) setPendingLocation(null);
      }
    })();

    return () => { cancelled = true; };
  }, [pendingLocation, countries]);

  useEffect(() => {
    // Note: this endpoint's JSON keys are all lowercase (see LowercaseNamingStrategy on
    // the backend) — no camelCase, e.g. `data.companyname`, not `data.companyName`.
    authApi.getBasicInfo().then(data => {
      if (!data) return;
      setForm(f => ({
        ...f,
        clientName:   data.companyname  || f.clientName,
        cinNumber:    data.cinnumber    || f.cinNumber,
        country:      data.country      || f.country,
        state:        data.state        || f.state,
        city:         data.city         || f.city,
        zipCode:      data.zipcode      || f.zipCode,
        industryType: data.industrytype || f.industryType,
        employeeSize: data.employeesize || f.employeeSize,
      }));

      if (Array.isArray(data.contacts) && data.contacts.length > 0) {
        // Restore previously-saved Contact Persons instead of the single blank row.
        setContacts(data.contacts.map(c => ({
          contactType: c.contacttype || '',
          contactName: c.contactname || '',
          designation: c.designation || '',
          email:       c.email       || '',
          phone:       c.phone       || '',
          mobile:      c.mobile      || '',
        })));
      } else {
        setContacts(cs => cs.map((c, i) => i !== 0 ? c : {
          ...c,
          contactName: data.clientname || c.contactName,
          email:       data.email      || c.email,
          phone:       data.phone      || c.phone,
          mobile:      data.phone      || c.mobile,
        }));
      }

      if (data.billing) {
        const isNA = v => v === NOT_APPLICABLE_VALUE;
        setBillTo(b => ({
          ...b,
          addr1: data.billing.addr1 || b.addr1,
          addr2: data.billing.addr2 || b.addr2,
          gstin: data.billing.gstin || b.gstin,
          pan:   data.billing.pan   || b.pan,
          tan:   data.billing.tan   || b.tan,
        }));
        setBillNA({
          gstin: isNA(data.billing.gstin),
          pan:   isNA(data.billing.pan),
          tan:   isNA(data.billing.tan),
        });
      }

      setTdsApplicable(String(data.tdsdeduction || '').toUpperCase() === 'YES');

      if (Array.isArray(data.documents)) {
        const byType = t => data.documents.find(d => d.type === t) || null;
        const toDoc = d => d ? { name: d.name, id: d.id } : null;
        setExistingDocs({
          gstin: toDoc(byType('GST Copy')),
          pan:   toDoc(byType('PAN Copy')),
          tan:   toDoc(byType('TAN Copy')),
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    onboardingApi.getCountryList().then(data => { if (Array.isArray(data)) setCountries(data); }).catch(() => {});
    onboardingApi.getDetails(1).then(res => {
      const d = res?.data || {};
      if (Array.isArray(d.industrytype)) setIndustryOptions(d.industrytype);
      if (d.pantanvalidationlabel) setPanLbl(d.pantanvalidationlabel);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.country) { setStates([]); return; }
    onboardingApi.getStateList(form.country).then(data => { if (Array.isArray(data)) setStates(data); }).catch(() => {});
  }, [form.country]);

  async function handleRegister() {
    // Compute every section's errors up front so all of them render together,
    // regardless of which check ends up failing the submit.
    const formErrs = {};
    if (!form.clientName.trim())   formErrs.clientName   = 'Required';
    if (!form.country.trim())      formErrs.country      = 'Required';
    if (!form.state.trim())        formErrs.state        = 'Required';
    if (!form.city.trim())         formErrs.city         = 'Required';
    if (!form.zipCode.trim())      formErrs.zipCode      = 'Required';
    if (!form.industryType.trim()) formErrs.industryType = 'Required';
    if (!form.employeeSize.trim()) formErrs.employeeSize = 'Required';
    setFormErrors(formErrs);

    const bG = billNA.gstin ? NOT_APPLICABLE_VALUE : billTo.gstin;
    const bP = billNA.pan   ? NOT_APPLICABLE_VALUE : billTo.pan;
    const bT = billNA.tan   ? NOT_APPLICABLE_VALUE : billTo.tan;
    const errors = {};
    if (!billTo.addr1.trim())      errors.addr1      = 'Required';
    if (!billTo.addr2.trim())      errors.addr2      = 'Required';
    if (!String(bG || '').trim()) errors.gstin = 'Required';
    if (!String(bP || '').trim()) errors.pan   = 'Required';
    if (!String(bT || '').trim()) errors.tan   = 'Required';
    if (!errors.gstin && !billNA.gstin && !GSTIN_RE.test(bG)) errors.gstin = 'Invalid GSTIN format';
    if (!errors.pan   && !billNA.pan   && !PAN_RE.test(bP))   errors.pan   = 'Invalid PAN format';
    if (!errors.tan   && !billNA.tan   && !TAN_RE.test(bT))   errors.tan   = 'Invalid TAN format';
    setBillErrors(errors);

    // GST document is only mandatory when GST isn't marked "not applicable", and only
    // if one isn't already on file from a previous save.
    const docErrs = {};
    if (!billNA.gstin && !docFiles.gstin && !existingDocs.gstin) docErrs.gstin = 'GST document is required';
    setDocErrors(docErrs);

    if (Object.keys(formErrs).length > 0) {
      addToast('Please fill in all the required fields', 'error');
      return;
    }
    if (!contacts.some(c => c.contactType === 'Client POC')) {
      addToast('At least one Contact Person must be Client POC.', 'error');
      return;
    }
    if (Object.keys(errors).length > 0) {
      addToast('Please fill in all the required Bill To details', 'error');
      return;
    }
    if (Object.keys(docErrs).length > 0) {
      addToast('Please upload the required GST document', 'error');
      return;
    }

    setSubmitting(true);
    try {
      // "Ship To" isn't shown in the UI — it's always saved identical to "Bill To".
      const body = {
        SalesClientContact: {
          ClientId:    clientId ? Number(clientId) : 0,
          ContactType: contacts.map(c => c.contactType || ''),
          ContactName: contacts.map(c => c.contactName || ''),
          Designation: contacts.map(c => c.designation || ''),
          Email:       contacts.map(c => c.email       || ''),
          Phone:       contacts.map(c => c.phone       || ''),
          Mobile:      contacts.map(c => c.mobile      || ''),
        },
        ClientModel: {
          ClientId:               clientId ? Number(clientId) : 0,
          State:                  form.state,
          Country:                form.country,
          ClientType:             'PREPAID',
          CheckasperSLA:          '',
          TDSDeduction:           tdsApplicable ? 'YES' : 'NO',
          ClientBillingInfoModel: { ClientType: 'PREPAID' },
          Clients: [{
            ClientName:    form.clientName,
            CINnumber:     form.cinNumber,
            City:          form.city,
            Zipcode:       form.zipCode,
            IndustryType:  form.industryType,
            EmployeeSize:  form.employeeSize,
            CheckasperSLA: '',
          }],
        },
        ClientBillingInfoModel: {
          BillToAddress1: billTo.addr1,
          BillToAddress2: billTo.addr2,
          BillCountry:    form.country,
          BillState:      form.state,
          BillCity:       form.city,
          BillPostalCode: form.zipCode,
          GSTINofBill:    bG,
          PANofBill:      bP,
          TANofBill:      bT,
          ShipToAddress1: billTo.addr1,
          ShipToAddress2: billTo.addr2,
          ShipCountry:    form.country,
          ShipState:      form.state,
          ShipCity:       form.city,
          ShipPostalCode: form.zipCode,
          GSTINofShip:    bG,
          PANofShip:      bP,
          TANofShip:      bT,
        },
      };
      const { ok, data } = await onboardingApi.registerCompanyStep1(body);
      if (!ok) { addToast(data?.message || 'Failed to save company details', 'error'); return; }

      // registercompanystep1 is a JSON endpoint with no file fields, so the GST/PAN/TAN
      // documents (if any were selected) go up as a separate multipart request.
      if (docFiles.gstin || docFiles.pan || docFiles.tan) {
        const fd = new FormData();
        if (docFiles.gstin) fd.append('GSTCopy', docFiles.gstin);
        if (docFiles.pan)   fd.append('PANCopy', docFiles.pan);
        if (docFiles.tan)   fd.append('TANCopy', docFiles.tan);
        const docRes = await onboardingApi.uploadClientRegistrationDocuments(fd);
        if (!docRes.ok) {
          addToast(docRes.data?.message || 'Company details saved, but document upload failed. Please try again.', 'error');
          return;
        }
      }

      onNavigate('digisign');
    } catch (e) {
      addToast(e.message || 'Failed to save company details', 'error');
    } finally { setSubmitting(false); }
  }

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showRegister={false} />
      <div className="auth-wrap" style={{ paddingTop: '20px' }}>
        <div style={{ width: '100%', maxWidth: '820px' }}>
          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', marginBottom: '28px' }}>
            {['Company Info', 'Agreement'].map((s, i) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: i === 0 ? 'var(--or)' : 'var(--g200)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: i === 0 ? 'var(--nv)' : 'var(--g400)' }}>{i + 1}</div>
                  <div style={{ fontSize: '10px', color: i === 0 ? 'var(--or-dk)' : 'var(--g400)', fontWeight: i === 0 ? 700 : 400, whiteSpace: 'nowrap' }}>{s}</div>
                </div>
                {i < 1 && <div style={{ width: '40px', height: '2px', background: 'var(--g200)', margin: '0 4px', marginBottom: '16px' }} />}
              </div>
            ))}
          </div>

          <div className="card" style={{ boxShadow: '0 8px 40px rgba(26,31,54,.13)' }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--nv)' }}>Register your company</div>
              <div style={{ fontSize: '13px', color: 'var(--g600)', marginTop: '4px' }}>Set up your cFIRST India Prepaid BGV account</div>
            </div>
            {/* GSTIN auto-fill */}
            <div style={{ background: 'var(--bl-lt)', border: '1px solid rgba(59,130,246,.25)', borderRadius: 'var(--rs)', padding: '14px 16px', marginBottom: '20px'}}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--nv)', marginBottom: '10px' }}>🔍 Auto-fill from GSTIN</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  className="inp" style={{ flex: 1, textTransform: 'uppercase' }} placeholder="Enter your 15-digit GSTIN" maxLength={15}
                  value={gstin} onChange={e => { setGstin(e.target.value.toUpperCase()); setGstResult(null); }}
                  onKeyDown={e => { if (e.key === 'Enter' && !gstFetching) handleFetchGstin(); }}
                />
                <button className="btn bpri bsm" onClick={handleFetchGstin} disabled={gstFetching} style={gstFetching ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                  {gstFetching ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Fetching…</> : 'Fetch Details'}
                </button>
              </div>
              {gstResult && (
                <div style={{ marginTop: '10px', padding: '10px', background: 'var(--gr-lt)', borderRadius: '6px', border: '1px solid rgba(13,179,107,.25)' }}>
                  <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--gr)', marginBottom: '4px' }}>✅ GSTIN verified — details auto-filled below</div>
                  <div style={{ fontSize: '11px', color: 'var(--g600)' }}>{[gstResult.legalName, gstResult.city, gstResult.state, gstResult.status].filter(Boolean).join(' · ')}</div>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
              <div className="fg fgfull">
                <label className="flbl">Client Name <span className="req">*</span></label>
                <input
                  className="inp" placeholder="Rajasthan Finserv Pvt. Ltd." value={form.clientName}
                  onChange={e => { set('clientName', e.target.value); if (formErrors.clientName) setFormErrors(fe => ({ ...fe, clientName: '' })); }}
                  style={formErrors.clientName ? { borderColor: 'var(--rd)' } : {}}
                />
                {formErrors.clientName && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{formErrors.clientName}</span>}
              </div>
              <div className="fg"><label className="flbl">CIN Number</label><input className="inp" value={form.cinNumber} onChange={e => set('cinNumber', e.target.value)} /></div>
              <div className="fg">
                <label className="flbl">Country <span className="req">*</span></label>
                <select
                  className="sel" value={form.country}
                  onChange={e => { set('country', e.target.value); set('state', ''); if (formErrors.country) setFormErrors(fe => ({ ...fe, country: '' })); }}
                  style={formErrors.country ? { borderColor: 'var(--rd)' } : {}}
                >
                  <option value="">-- Select Country --</option>
                  {countries.map(c => <option key={c.value ?? c.Value} value={c.value ?? c.Value}>{c.text ?? c.Text}</option>)}
                </select>
                {formErrors.country && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{formErrors.country}</span>}
              </div>
              <div className="fg">
                <label className="flbl">State <span className="req">*</span></label>
                <select
                  className="sel" value={form.state}
                  onChange={e => { set('state', e.target.value); if (formErrors.state) setFormErrors(fe => ({ ...fe, state: '' })); }}
                  style={formErrors.state ? { borderColor: 'var(--rd)' } : {}}
                >
                  <option value="">-- Select State --</option>
                  {states.map(s => <option key={s.value ?? s.Value} value={s.value ?? s.Value}>{s.text ?? s.Text}</option>)}
                </select>
                {formErrors.state && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{formErrors.state}</span>}
              </div>
              <div className="fg">
                <label className="flbl">City <span className="req">*</span></label>
                <input
                  className="inp" value={form.city}
                  onChange={e => { set('city', e.target.value); if (formErrors.city) setFormErrors(fe => ({ ...fe, city: '' })); }}
                  style={formErrors.city ? { borderColor: 'var(--rd)' } : {}}
                />
                {formErrors.city && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{formErrors.city}</span>}
              </div>
              <div className="fg">
                <label className="flbl">Zip Code <span className="req">*</span></label>
                <input
                  className="inp" value={form.zipCode}
                  onChange={e => { set('zipCode', e.target.value); if (formErrors.zipCode) setFormErrors(fe => ({ ...fe, zipCode: '' })); }}
                  style={formErrors.zipCode ? { borderColor: 'var(--rd)' } : {}}
                />
                {formErrors.zipCode && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{formErrors.zipCode}</span>}
              </div>
              <div className="fg">
                <label className="flbl">Industry <span className="req">*</span></label>
                <select
                  className="sel" value={form.industryType}
                  onChange={e => { set('industryType', e.target.value); if (formErrors.industryType) setFormErrors(fe => ({ ...fe, industryType: '' })); }}
                  style={formErrors.industryType ? { borderColor: 'var(--rd)' } : {}}
                >
                  <option value="">-- Select Industry --</option>
                  {industryOptions.map(o => <option key={o.value ?? o.Value} value={o.value ?? o.Value}>{o.text ?? o.Text}</option>)}
                </select>
                {formErrors.industryType && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{formErrors.industryType}</span>}
              </div>
              <div className="fg">
                <label className="flbl">Employee Size <span className="req">*</span></label>
                <select
                  className="sel" value={form.employeeSize}
                  onChange={e => { set('employeeSize', e.target.value); if (formErrors.employeeSize) setFormErrors(fe => ({ ...fe, employeeSize: '' })); }}
                  style={formErrors.employeeSize ? { borderColor: 'var(--rd)' } : {}}
                >
                  {REG_EMPLOYEE_SIZES.map(s => <option key={s} value={s}>{s || '-- Select Employee Size --'}</option>)}
                </select>
                {formErrors.employeeSize && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{formErrors.employeeSize}</span>}
              </div>
            </div>

            {/* Contacts table */}
            <div style={{ marginTop: '22px' }}>
              <div className="flex jb aic mb12" style={{ gap: 12 }}>
                <div className="flex aic" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)' }}>Contact Persons</div>
                  <span style={{
                    fontSize: '11px', fontWeight: 500, color: 'var(--bl)', background: 'rgba(59,130,246,0.1)',
                    padding: '3px 8px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '4px'
                  }}>
                    ⓘ Only Client POC is mandatory for contact details
                  </span>
                </div>
                <button className="btn bout bsm" onClick={addContact}>+ Add Contact</button>
              </div>
              <div className="twrap">
                <table>
                  <thead>
                    <tr>
                      <th>Contact Type</th>
                      <th>Contact Name</th>
                      <th>Designation</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Mobile</th>
                      <th style={{ width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c, i) => (
                      <tr key={i}>
                        <td>
                          <select className="fsel" style={{ minWidth: 120 }} value={c.contactType} onChange={e => setContact(i, 'contactType', e.target.value)}>
                            <option value="">Select</option>
                            {REG_CONTACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td><input className="inp" style={{ minWidth: 110 }} value={c.contactName} placeholder="Contact Name" onChange={e => setContact(i, 'contactName', e.target.value)} /></td>
                        <td><input className="inp" style={{ minWidth: 100 }} value={c.designation} placeholder="Designation" onChange={e => setContact(i, 'designation', e.target.value)} /></td>
                        <td><input className="inp" type="email" style={{ minWidth: 150 }} value={c.email} placeholder="Email" onChange={e => setContact(i, 'email', e.target.value)} /></td>
                        <td><input className="inp" style={{ minWidth: 90 }} value={c.phone} maxLength={13} placeholder="Phone No" onChange={e => setContact(i, 'phone', e.target.value)} /></td>
                        <td><input className="inp" style={{ minWidth: 90 }} value={c.mobile} maxLength={13} placeholder="Mobile No" onChange={e => setContact(i, 'mobile', e.target.value)} /></td>
                        <td>
                          {contacts.length > 1 && (
                            <button className="btn bghost bsm" style={{ color: 'var(--rd)', padding: '4px 8px' }} onClick={() => removeContact(i)}>✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bill To */}
            <div style={{ marginTop: '22px', marginBottom: '4px', fontSize: '13px', fontWeight: 700, color: 'var(--nv)' }}>Billing information</div>
            <div style={{ fontSize: '11px', color: 'var(--g600)', marginBottom: '8px' }}>Uses the Country / State / City entered above.</div>
            <hr className="hr" style={{ margin: '0 0 12px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 16px' }}>
              <BillingField label="Address 1" req maxLength={100} value={billTo.addr1} onChange={v => setBillToF('addr1', v)} error={billErrors.addr1} />
              <BillingField label="Address 2" req maxLength={100} value={billTo.addr2} onChange={v => setBillToF('addr2', v)} error={billErrors.addr2} />
              <NAField label="GSTIN" req value={billTo.gstin} naChecked={billNA.gstin} naLabel={panLbl}
                onChange={v => setBillToF('gstin', v)} onNA={c => handleBillNA('gstin', c)} error={billErrors.gstin} />
              <NAField label="PAN" req value={billTo.pan} naChecked={billNA.pan} naLabel={panLbl}
                onChange={v => setBillToF('pan', v)} onNA={c => handleBillNA('pan', c)} error={billErrors.pan} />
              <NAField label="TAN" req value={billTo.tan} naChecked={billNA.tan} naLabel={panLbl}
                onChange={v => setBillToF('tan', v)} onNA={c => handleBillNA('tan', c)} error={billErrors.tan} />
            </div>

            {/* GST / PAN / TAN document uploads */}
            <div style={{ marginTop: '18px', marginBottom: '4px', fontSize: '13px', fontWeight: 700, color: 'var(--nv)' }}>Documents</div>
            <div style={{ fontSize: '11px', color: 'var(--g600)', marginBottom: '8px' }}>
              GST document is required unless GST is marked {panLbl.replace('?', '')}.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 16px' }}>
              <FileField label="GST Copy" req={!billNA.gstin} file={docFiles.gstin} disabled={billNA.gstin}
                onChange={f => setDocFile('gstin', f)} error={docErrors.gstin}
                existing={existingDocs.gstin} changing={docChanging.gstin}
                onToggleChange={c => setDocChangingF('gstin', c)} onDownload={() => downloadExistingDoc(existingDocs.gstin.name)} />
              <FileField label="PAN Copy" file={docFiles.pan} disabled={billNA.pan}
                onChange={f => setDocFile('pan', f)}
                existing={existingDocs.pan} changing={docChanging.pan}
                onToggleChange={c => setDocChangingF('pan', c)} onDownload={() => downloadExistingDoc(existingDocs.pan.name)} />
              <FileField label="TAN Copy" file={docFiles.tan} disabled={billNA.tan}
                onChange={f => setDocFile('tan', f)}
                existing={existingDocs.tan} changing={docChanging.tan}
                onToggleChange={c => setDocChangingF('tan', c)} onDownload={() => downloadExistingDoc(existingDocs.tan.name)} />
            </div>

            {/* TDS applicable for invoice — requires a valid GSTIN */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '18px', fontSize: '12.5px', color: gstValid ? 'var(--g700)' : 'var(--g400)', cursor: gstValid ? 'pointer' : 'not-allowed' }}
              title={gstValid ? undefined : 'Add a valid GSTIN to enable TDS'}>
              <input type="checkbox" checked={tdsApplicable} disabled={!gstValid} onChange={e => setTdsApplicable(e.target.checked)} />
              TDS applicable for invoice
            </label>

            <button className="btn bpri wf blg mt16" onClick={handleRegister} disabled={submitting} style={submitting ? { cursor: 'not-allowed', opacity: 0.92 } : {}}>
              {submitting ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  <span style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block', flexShrink: 0 }} />
                  Saving…
                </span>
              ) : 'Save & Continue to Agreement →'}
            </button>
            {/* <div style={{ textAlign: 'center', marginTop: '14px', fontSize: '12.5px', color: 'var(--g600)' }}>
              Already have an account? <span style={{ color: 'var(--or-dk)', fontWeight: 600, cursor: 'pointer' }} onClick={() => onNavigate('login')}>Login</span>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
}

// Check-type category helpers for the À La Carte pricing table (mirrors the
// categorisation used on the admin-facing ClientOnBoarding pricing tab).
function deriveAlacarteCategory(checktype) {
  const n = (checktype || '').toLowerCase().trim();
  if (n.includes('address'))          return 'Address';
  if (n.startsWith('alcohol'))        return 'Alcohol';
  if (n.startsWith('criminal'))       return 'Criminal';
  if (n.startsWith('credential'))     return 'Credential';
  if (n.startsWith('database'))       return 'Database';
  if (n.startsWith('drug'))           return 'Drug Test';
  if (n === 'education')              return 'Education';
  if (n === 'directorship' || n.startsWith('employment') ||
      n === 'gap check'    || n === 'mca'     || n === 'psychometric' ||
      n === 'reference'    || n === 'resume'  ||
      n === 'right to work' || n === 'work authorization') return 'Employment';
  if (n === 'adverse media' || n === 'bank statement' || n.startsWith('bankruptcy') ||
      n === 'credit'        || n.startsWith('financial') ||
      n === 'form 16'       || n === 'form 26as')          return 'Finance';
  if (n.startsWith('identity') || n === 'uan' || n.startsWith('pan to')) return 'Identity';
  return 'Other';
}
function alacarteCatChipClass(cat) {
  const map = {
    Address: 'cn', Alcohol: 'ca', Criminal: 'cr', Credential: 'cb',
    Database: 'cb', 'Drug Test': 'ca', Education: 'cg', Employment: 'cg',
    Finance: 'cr', Identity: 'cb', Other: 'cgr',
  };
  return map[cat] || 'cn';
}

// Signature fonts loaded via the Google Fonts link in public/index.html.
const SIG_FONT_STYLES = [
  { font: "'Great Vibes',cursive",    size: 52, label: 'Classic' },
  { font: "'Dancing Script',cursive", size: 42, label: 'Elegant' },
  { font: "'Pinyon Script',cursive",  size: 50, label: 'Formal' },
  { font: "'Alex Brush',cursive",     size: 44, label: 'Refined' },
  { font: "'Allura',cursive",         size: 46, label: 'Graceful' },
  { font: "'Italianno',cursive",      size: 52, label: 'Italian' },
  { font: "'Tangerine',cursive",      size: 56, label: 'Thin' },
  { font: "'Sacramento',cursive",     size: 46, label: 'Minimal' },
  { font: "'Kaushan Script',cursive", size: 36, label: 'Bold' },
  { font: "'Satisfy',cursive",        size: 36, label: 'Fluid' },
  { font: "'Yellowtail',cursive",     size: 36, label: 'Rounded' },
  { font: "'Courgette',cursive",      size: 34, label: 'Sporty' },
  { font: "'Marck Script',cursive",   size: 34, label: 'Handwritten' },
  { font: "'Pacifico',cursive",       size: 28, label: 'Casual' },
  { font: "'Lobster',cursive",        size: 30, label: 'Display' },
];

// ── DigiSign Page (Agreement — step 2 of the post-login wizard) ──────────
export function DigiSign({ onNavigate }) {
  const { user, clientId, addToast } = useApp();
  const companyName = user?.ClientName || '';
  const [signerName, setSignerName] = useState(user?.UserDescription || '');
  const [sigStyleIdx, setSigStyleIdx] = useState(0);
  const [pendingStyleIdx, setPendingStyleIdx] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedAt] = useState(() => new Date());
  const [downloading, setDownloading] = useState(false);

  // Agreement preview — merged HTML fetched fresh from the backend (see
  // onboardingApi.getAgreementHtmlAlacarte) so it always reflects the current docx template.
  // The checkbox below stays disabled until the user scrolls this box to the bottom.
  const [agreementHtml, setAgreementHtml] = useState('');
  const [agreementLoading, setAgreementLoading] = useState(true);
  const [agreementError, setAgreementError] = useState(false);
  const [agreementRead, setAgreementRead] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const agreementBoxRef = useRef(null); // the fixed-size scrollable preview box (stable width, unaffected by scaling)
  const agreementDocRef = useRef(null);
  const [agreementDocSize, setAgreementDocSize] = useState(null); // { width, height, scale }

  useEffect(() => {
    setAgreementLoading(true);
    setAgreementError(false);
    onboardingApi.getAgreementHtmlAlacarte()
      .then(res => {
        const html = res?.html || '';
        setAgreementHtml(html);
        // Nothing to scroll (empty/short agreement) — don't leave the checkbox stuck locked.
        if (!html.trim()) setAgreementRead(true);
      })
      .catch(() => setAgreementError(true))
      .finally(() => setAgreementLoading(false));
  }, []);

  // The agreement docx's usable page content width (11909-twip page, 864/720-twip
  // margins — same in both the Prepaid and Postpaid templates): 793.9px page minus
  // 57.6px + 48px margins. The signature block (seal image + "Name/Designation/Date"
  // lines) is built from tab stops and a paragraph indent, not a real table, so Aspose's
  // HTML export positions them with pixel offsets that only line up correctly at this
  // exact width — reflow it narrower (max-width:100%) and the seal overlaps the text;
  // let it shrink-to-fit (max-content) and the pricing table's percentage-width columns
  // lose their sizing basis and blow up huge. So render the doc at this fixed width
  // (matching what it was authored for) and scale the whole block down uniformly to
  // fit the preview box, like a print-preview thumbnail.
  const AGREEMENT_PAGE_WIDTH = 688;

  useLayoutEffect(() => {
    if (!agreementHtml) { setAgreementDocSize(null); return; }
    function measure() {
      const el = agreementDocRef.current;
      const box = agreementBoxRef.current;
      if (!el || !box) return;
      const naturalHeight = el.scrollHeight;
      const boxWidth = box.clientWidth - 48; // minus the box's 24px left/right padding
      if (!naturalHeight || !boxWidth) return;
      const scale = Math.min(1, boxWidth / AGREEMENT_PAGE_WIDTH);
      setAgreementDocSize({ width: AGREEMENT_PAGE_WIDTH, height: naturalHeight, scale });
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [agreementHtml]);

  function handleAgreementScroll(e) {
    const el = e.target;
    const max = el.scrollHeight - el.clientHeight;
    const ratio = max > 0 ? Math.min(el.scrollTop / max, 1) : 1;
    setScrollPct(Math.round(ratio * 100));
    if (!agreementRead && ratio >= 0.98) setAgreementRead(true);
  }

  const ALACARTE_PAGE_SIZE = 10;
  const [pricingAlacarte, setPricingAlacarte] = useState([]);
  const [alacarteSearch, setAlacarteSearch] = useState('');
  const [alacarteCategory, setAlacarteCategory] = useState('');
  const [alacarteShowAll, setAlacarteShowAll] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    debugger;
    // Response items are lowercase-keyed: { text, value, price, currency, mainchecktype }.
    onboardingApi.getAlacarteDetails()
      .then(data => setPricingAlacarte(Array.isArray(data)
        ? data.map(d => ({ checktype: d.text, price: d.price, currency: d.currency, countryname: d.countryname }))
        : []))
      .catch(() => {});
  }, [clientId]);

  const pricingWithCat = pricingAlacarte.map(p => ({ ...p, category: p.category || deriveAlacarteCategory(p.checktype) }));
  const alacarteCategories = [...new Set(pricingWithCat.map(p => p.category))].sort();
  const filteredAlacarte = pricingWithCat.filter(p => {
    const matchSearch = !alacarteSearch || (p.checktype || '').toLowerCase().includes(alacarteSearch.toLowerCase());
    const matchCat = !alacarteCategory || p.category === alacarteCategory;
    return matchSearch && matchCat;
  });
  const displayedAlacarte = alacarteShowAll ? filteredAlacarte : filteredAlacarte.slice(0, ALACARTE_PAGE_SIZE);

  // Collapse back to the first page whenever the visible set changes underneath the toggle.
  useEffect(() => { setAlacarteShowAll(false); }, [alacarteSearch, alacarteCategory]);

  const activeStyle = SIG_FONT_STYLES[sigStyleIdx];
  const displayName = signerName.trim() || 'Your Name';

  // Same trimmed-PNG data URL that gets saved to the backend on signing (renderSignatureImage,
  // below) — reused here so the agreement preview shows the actual pixels that will end up on
  // the signed PDF, not a CSS approximation. Regenerated whenever the name/style changes.
  const [signPreviewUrl, setSignPreviewUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    renderSignatureImage().then(url => { if (!cancelled) setSignPreviewUrl(url); });
    return () => { cancelled = true; };
  }, [displayName, activeStyle]);

  // The unsigned preview HTML carries SIGN_PREVIEW_MARKER where the ##SIGN## tag sat in the
  // template (see OnBoardingRepository.GetAgreementHtmlAlacarte) — swap it for the live-rendered
  // signature image so it shows up in-place in the rendered agreement rather than as blank space.
  // Capped to 120x48px (~90x36pt at 96dpi) to match the fixed size ReplaceWithImageEvaluator gives
  // the real signature image on the signed PDF.
  const SIGN_PREVIEW_BOX = { width: 120, height: 48 };
  const withSignImage = agreementHtml.includes(SIGN_PREVIEW_MARKER)
    ? agreementHtml.replace(
        SIGN_PREVIEW_MARKER,
        signPreviewUrl
          ? `<img src="${signPreviewUrl}" alt="signature preview" style="display:inline-block;max-width:${SIGN_PREVIEW_BOX.width}px;max-height:${SIGN_PREVIEW_BOX.height}px;object-fit:contain;vertical-align:middle;" />`
          : ''
      )
    : agreementHtml;
  // Same idea as SIGN_PREVIEW_MARKER, but for the ##SIGNNAME## tag — swapped for the
  // typed name (HTML-escaped, since this is dropped into dangerouslySetInnerHTML).
  const renderedAgreementHtml = withSignImage.includes(SIGNNAME_PREVIEW_MARKER)
    ? withSignImage.replace(
        SIGNNAME_PREVIEW_MARKER,
        displayName.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      )
    : withSignImage;

  function openStylePicker() { setPendingStyleIdx(sigStyleIdx); setPickerOpen(true); }
  function confirmStyle() { setSigStyleIdx(pendingStyleIdx); setPickerOpen(false); }

  async function downloadAgreement() {
    setDownloading(true);
    try {
      // New clients only have à la carte pricing (no packages), so this page uses the
      // alacarte-specific agreement endpoint rather than onboardingApi.downloadAgreement.
      const { blob, filename } = await onboardingApi.downloadAgreementAlacarte();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { addToast('Download failed: ' + e.message, 'error'); }
    finally { setDownloading(false); }
  }

  // Renders the signature preview (same font/text shown on screen) onto an offscreen
  // canvas so it can be saved as a PNG and embedded at ##SIGN## in the agreement PDF.
  // The name is first drawn on an oversized canvas, then cropped tightly to its ink
  // bounding box so no blank margin is saved around the signature.
  async function renderSignatureImage() {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const font = `${activeStyle.size * 2}px ${activeStyle.font}`;
    try { await document.fonts.load(font); } catch { /* fall back to default font if it fails to load */ }
    ctx.fillStyle = '#1a1f36';
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(displayName, canvas.width / 2, canvas.height / 2);

    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let minX = canvas.width, minY = canvas.height, maxX = -1, maxY = -1;
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const i = (y * canvas.width + x) * 4;
        // Anything noticeably darker than the white background counts as ink.
        if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX || maxY < minY) return canvas.toDataURL('image/png'); // nothing drawn — fall back as-is

    const pad = 6;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(canvas.width - 1, maxX + pad);
    maxY = Math.min(canvas.height - 1, maxY + pad);
    const w = maxX - minX + 1, h = maxY - minY + 1;

    const trimmed = document.createElement('canvas');
    trimmed.width = w;
    trimmed.height = h;
    const tctx = trimmed.getContext('2d');
    tctx.fillStyle = '#ffffff';
    tctx.fillRect(0, 0, w, h);
    tctx.drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
    return trimmed.toDataURL('image/png');
  }

  async function handleSign() {
    if (!signerName.trim()) { addToast('Please enter your full name', 'error'); return; }
    if (!agreementRead) { addToast('Please read the full agreement before signing', 'error'); return; }
    if (!agreed) { addToast('Please accept the agreement', 'error'); return; }
    setSigning(true);
    try {
      const signatureImage = await renderSignatureImage();
      await onboardingApi.saveSignature(signatureImage, signerName.trim()).catch(() => {});
      // Stores a copy of the signed agreement PDF (same content as the download button)
      // via the existing onboarding attachment pipeline, and marks onboarding approved.
      await onboardingApi.signAndActivateAgreement();
      addToast('Account activated! 🎉', 'success');
      onNavigate('dashboard');
    }
    catch (e) {
      // Surface the failure instead of silently navigating to the dashboard — this call
      // is what actually stores the agreement copy and approves onboarding, so pretending
      // it succeeded when it didn't hides a real problem from the user.
      addToast(e.message || 'Failed to activate your account. Please try again.', 'error');
    }
    finally { setSigning(false); }
  }

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showLogin showRegister={false} />
      <div className="pg" style={{ maxWidth: '760px' }}>
        <WizardSteps steps={['Company Info', 'Agreement']} current={1} />
        <div className="card" style={{ boxShadow: '0 8px 40px rgba(26,31,54,.13)' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--nv)' }}>Review & Sign BGV Agreement</div>
          <div style={{ fontSize: '12.5px', color: 'var(--g600)', marginTop: '3px' }}>Please review the agreed check types, pricing, and terms before signing</div>
        </div>

        {/* A La Carte — check types */}
        <div style={{ background: 'var(--wh)', border: '1px solid var(--g200)', borderRadius: 'var(--r)', overflow: 'hidden', marginBottom: '18px' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--g100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--nv)' }}>🛒 A La Carte — Check Types</div>
            <div className="flex g8 aic">
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--g400)' }}>🔍</span>
                <input
                  className="inp" placeholder="Search…" value={alacarteSearch} onChange={e => setAlacarteSearch(e.target.value)}
                  style={{ paddingLeft: '28px', fontSize: '12px', height: '32px', width: '160px' }}
                />
              </div>
              {alacarteCategories.length > 0 && (
                <select className="sel" value={alacarteCategory} onChange={e => setAlacarteCategory(e.target.value)} style={{ fontSize: '12px', height: '32px', padding: '0 8px' }}>
                  <option value="">All Categories</option>
                  {alacarteCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              <span style={{ fontSize: '11px', color: 'var(--g400)', whiteSpace: 'nowrap' }}>{filteredAlacarte.length} check{filteredAlacarte.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="twrap">
            <table>
              <thead><tr><th>Check Type</th><th>Price</th><th>Country</th><th>Currency</th><th>Category</th></tr></thead>
              <tbody>
                {filteredAlacarte.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '30px', textAlign: 'center', color: 'var(--g400)', fontSize: '12.5px' }}>
                    {pricingWithCat.length === 0 ? 'No À La Carte pricing configured yet.' : 'No checks found'}
                  </td></tr>
                ) : displayedAlacarte.map((p, i) => (
                  <tr key={i}>
                    <td className="td1" style={{ textTransform: 'capitalize' }}>{(p.checktype || '').toLowerCase() || '—'}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', background: 'var(--or-lt)', border: '1px solid rgba(245,166,35,.4)', borderRadius: '50px', fontSize: '12px', fontWeight: 700, color: 'var(--or-dk)' }}>₹{p.price || '—'}</span>
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{p.countryname || '—'}</td>
                    <td>
                      <span style={{ display: 'inline-block', background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: '5px', fontSize: '11px', fontWeight: 600, color: 'var(--g600)', padding: '2px 8px' }}>{p.currency || '—'}</span>
                    </td>
                    <td>{p.category ? <span className={`chip ${alacarteCatChipClass(p.category)}`}>{p.category}</span> : <span style={{ color: 'var(--g400)' }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredAlacarte.length > ALACARTE_PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px', borderTop: '1px solid var(--g100)' }}>
              <button className="btn bout bsm" onClick={() => setAlacarteShowAll(s => !s)}>
                {alacarteShowAll ? '▲ Show Less' : `▼ Show More (${filteredAlacarte.length - ALACARTE_PAGE_SIZE} more)`}
              </button>
            </div>
          )}
        </div>

        {/* Key terms */}
        <div style={{ background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 'var(--r)', padding: '16px 20px', marginBottom: '18px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--nv)', marginBottom: '12px' }}>📜 Key Terms of Agreement</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {['Prepaid wallet model — credits purchased in advance; deducted per order', 'Auto-refund — invited candidates not submitting within 7 days trigger a full refund', 'GST invoices — issued within 24 hrs of each top-up; input credit eligible', 'Candidate consent — client is responsible for obtaining written consent before ordering', 'SLA — Standard 7 working days; Premium 10 working days', 'Data retention — reports stored for 12 months; deleted thereafter per data policy'].map(t => (
              <div key={t} style={{ fontSize: '12px', color: 'var(--g600)', display: 'flex', gap: '8px' }}>
                <span style={{ color: 'var(--gr)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                <span dangerouslySetInnerHTML={{ __html: t.replace('—', '<strong>') + '</strong>' }} />
              </div>
            ))}
          </div>
          <button
            onClick={downloadAgreement} disabled={downloading}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', marginTop: '14px', background: 'none', border: 'none', padding: 0,
              fontSize: '12px', fontWeight: 600, color: 'var(--or-dk)', cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.6 : 1,
            }}
          >
            {downloading ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Downloading…</> : '📄 Download full agreement PDF →'}
          </button>
        </div>

        {/* Digital Signature block */}
        <div style={{ background: 'linear-gradient(135deg,#f0fdf8,#e6faf3)', border: '1.5px solid rgba(13,179,107,.3)', borderRadius: 'var(--r)', padding: '20px 22px', marginBottom: '18px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--nv)', marginBottom: '4px' }}>✍️ Digital Signature</div>
          <div style={{ fontSize: '12px', color: 'var(--g600)', marginBottom: '16px' }}>Type your full name below to generate your signature. Select a style and it will appear in the signature box.</div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '18px' }}>
            <div className="fg" style={{ flex: 1, marginBottom: 0 }}>
              <label className="flbl">Your Full Name <span className="req">*</span></label>
              <input className="inp" placeholder="e.g. Rohit Sharma" value={signerName} onChange={e => setSignerName(e.target.value)} style={{ fontSize: '14px' }} />
            </div>
            <button className="btn bout bsm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={openStylePicker}>↻ Regenerate Signatures</button>
          </div>

          {/* Agreement preview — must be scrolled to the end to unlock the consent checkbox */}
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '8px' }}>📄 Preview Agreement — Scroll to read fully</div>
          <div
            ref={agreementBoxRef}
            onScroll={agreementLoading || agreementError ? undefined : handleAgreementScroll}
            style={{ background: '#fff', border: '1.5px solid var(--g200)', borderRadius: '10px', height: '340px', overflowY: 'auto', overflowX: 'hidden', padding: '20px 24px', fontSize: '12.5px', color: '#2D3352', lineHeight: 1.8, marginBottom: '8px' }}
          >
            {agreementLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--g400)', fontSize: '12px', gap: '8px' }}>
                <span className="spinner" style={{ width: 14, height: 14 }} /> Loading agreement…
              </div>
            ) : agreementError ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: 'var(--rd)', fontSize: '12px', textAlign: 'center' }}>
                <div>⚠️ Couldn't load the agreement. Please try again.</div>
                <button className="btn bout bsm" onClick={() => {
                  setAgreementLoading(true); setAgreementError(false);
                  onboardingApi.getAgreementHtmlAlacarte()
                    .then(res => { const html = res?.html || ''; setAgreementHtml(html); if (!html.trim()) setAgreementRead(true); })
                    .catch(() => setAgreementError(true))
                    .finally(() => setAgreementLoading(false));
                }}>↻ Retry</button>
              </div>
            ) : (
              <div
                style={agreementDocSize
                  ? { width: agreementDocSize.width * agreementDocSize.scale, height: agreementDocSize.height * agreementDocSize.scale, overflow: 'hidden' }
                  : { visibility: 'hidden' }}
              >
                <div
                  ref={agreementDocRef}
                  className="agreement-doc"
                  style={{
                    width: AGREEMENT_PAGE_WIDTH,
                    transform: agreementDocSize ? `scale(${agreementDocSize.scale})` : undefined,
                    transformOrigin: 'top left',
                  }}
                  dangerouslySetInnerHTML={{ __html: renderedAgreementHtml }}
                />
              </div>
            )}
          </div>

          {/* Scroll progress */}
          <div style={{ height: '3px', background: 'var(--g200)', borderRadius: '3px', overflow: 'hidden', marginBottom: '6px' }}>
            <div style={{ height: '100%', background: agreementRead ? 'var(--gr)' : 'var(--or)', borderRadius: '3px', width: scrollPct + '%', transition: 'width .15s' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: agreementRead ? 'var(--gr)' : 'var(--or-dk)', display: 'flex', alignItems: 'center', gap: '5px' }}>
              {agreementRead ? '✓ Fully read — checkbox unlocked' : '↓ Scroll down to read the full agreement and unlock the checkbox'}
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--g600)' }}>{scrollPct}%</div>
          </div>

          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '8px' }}>Signature Preview</div>
          <div style={{ background: '#fff', border: '1.5px solid rgba(13,179,107,.4)', borderRadius: '10px', minHeight: '90px', alignItems: 'center', justifyContent: 'center', position: 'relative', marginBottom: '4px' }}>
            <div style={{ fontSize: activeStyle.size + 'px', fontFamily: activeStyle.font, color: 'var(--nv)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', lineHeight: 1.6, padding: '10px 0' }}>{displayName}</div>
            <div style={{ position: 'absolute', bottom: '5px', right: '12px', fontSize: '9px', color: 'var(--g400)', fontFamily: 'monospace' }}>
              {signedAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} {signedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ fontSize: '10.5px', color: 'var(--g400)' }}>🔒 Bound to agreement with timestamp</div>
            <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--or-dk)', background: 'var(--or-lt)', padding: '2px 9px', borderRadius: '20px', border: '1px solid rgba(245,166,35,.3)' }}>{activeStyle.label}</div>
          </div>

          <div style={{
            padding: '12px 14px', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '10px', transition: 'all .3s',
            background: agreementRead ? 'rgba(255,255,255,.8)' : 'var(--g100)',
            border: agreementRead ? '1px solid rgba(13,179,107,.2)' : '1.5px solid var(--g200)',
            opacity: agreementRead ? 1 : 0.6,
          }}>
            <input type="checkbox" disabled={!agreementRead} style={{ marginTop: '3px', accentColor: 'var(--or)', cursor: agreementRead ? 'pointer' : 'not-allowed' }} checked={agreed} onChange={e => setAgreed(e.target.checked)} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '11.5px', color: 'var(--g600)', lineHeight: 1.6 }}>
                I, <strong>{displayName}</strong>{companyName && <>, authorised representative of <strong>{companyName}</strong></>}, have read, understood, and agree to the BGV service agreement with SwiftCheq powered by cFIRST. I confirm this digital signature is legally binding.
              </span>
              {!agreementRead && (
                <div style={{ marginTop: '6px', fontSize: '10.5px', fontWeight: 600, color: 'var(--rd)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  🔒 Please read the entire agreement above to unlock this checkbox
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Signature style picker modal */}
        {pickerOpen && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,31,54,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setPickerOpen(false)}
          >
            <div
              style={{ background: 'var(--wh)', borderRadius: '16px', boxShadow: '0 16px 48px rgba(26,31,54,.25)', width: '100%', maxWidth: '620px', margin: '20px', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--g100)', flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--nv)' }}>✍️ Choose your signature style</div>
                  <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '2px' }}>{SIG_FONT_STYLES.length} professional styles — select one to use</div>
                </div>
                <button onClick={() => setPickerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--g400)', padding: '4px', lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1px', background: 'var(--g100)', overflowY: 'auto', flex: 1 }}>
                {SIG_FONT_STYLES.map((s, i) => (
                  <div
                    key={s.label} onClick={() => setPendingStyleIdx(i)}
                    style={{
                      background: i === pendingStyleIdx ? 'var(--or-xlt)' : '#fff', padding: '14px 10px', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minHeight: '88px', justifyContent: 'center',
                      outline: i === pendingStyleIdx ? '2px solid var(--or)' : 'none', outlineOffset: '-2px',
                    }}
                  >
                    <div style={{ fontFamily: s.font, fontSize: s.size + 'px', color: 'var(--nv)', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '180px', textOverflow: 'ellipsis', textAlign: 'center', lineHeight: 1.6, padding: '6px 0' }}>{displayName}</div>
                    <div style={{ fontSize: '9.5px', fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ padding: '14px 22px', borderTop: '1px solid var(--g100)', display: 'flex', gap: '9px', justifyContent: 'flex-end', flexShrink: 0 }}>
                <button className="btn bout bsm" onClick={() => setPickerOpen(false)}>Cancel</button>
                <button className="btn bpri bsm" onClick={confirmStyle}>✓ Use This Signature</button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn bout" onClick={() => onNavigate('register')}>← Back</button>
          <button className="btn bpri blg" style={{ flex: 1, ...(signing ? { cursor: 'not-allowed', opacity: 0.92 } : {}) }} onClick={handleSign} disabled={signing}>
            {signing ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <span style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block', flexShrink: 0 }} />
                Activating…
              </span>
            ) : '✏️ Sign & Activate Account →'}
          </button>
        </div>
        </div>
      </div>
    </>
  );
}
